import useAuth from "./hooks/useAuth";
import { useEffect, useState, useCallback, useRef } from "react";
import CategoryDropdownScores from "./CategoryDropdownScores";
import Select from 'react-select';
import ChartsAreaIntensity from './ChartsAreaIntensity';

const UserSentencesIntensity = ({ language, date }) => {
    const { auth, setAuth } = useAuth();
    const [stats, setStats] = useState();
    const [scoreType, setScoreType] = useState('AccuracyScore');
    const [lowlimit, setLowlimit] = useState();
    const [loading, setLoading] = useState(false);
    const [chartsLoading, setChartsLoading] = useState(false);
    const refreshTimeoutRef = useRef();
    
    // Effect for initial load
    useEffect(() => { 
        getMyStats();
    }, [language, date]);

    // Debounced effect for filter changes
    useEffect(() => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        // Only refresh if we already have data (not initial load)
        if (stats) {
            setChartsLoading(true);
            refreshTimeoutRef.current = setTimeout(() => {
                getMyStats();
            }, 500); // 500ms debounce
        }

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [scoreType, lowlimit]);

    const getMyStats = async () => {
        const token = localStorage.getItem("token");
        setLoading(true);
        
        try {
            let query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/${scoreType}/sentences/dynamics_intensity`;
            
            const params = new URLSearchParams();
            if (lowlimit) params.append('lowerlimit', lowlimit);
            // Remove checkuser parameter since this is for the current user
            
            if (params.toString()) {
                query += `?${params.toString()}`;
            }

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const json = await response.json();
                setStats(json);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error fetching intensity stats:", error);
        } finally {
            setLoading(false);
            setChartsLoading(false);
        }
    };

    const handleScoreTypeChange = useCallback((e) => {
        setScoreType(e.value);
    }, []);

    const handleLowlimitChange = useCallback((e) => {
        setLowlimit(e.value);
    }, []);

    // Format score type for display
    const getScoreTypeDisplayName = (scoreType) => {
        const displayNames = {
            'AccuracyScore': 'Accuracy Score',
            'FluencyScore': 'Fluency Score', 
            'CompletenessScore': 'Completeness Score',
            'PronScore': 'Pronunciation Score'
        };
        return displayNames[scoreType] || scoreType;
    };

    if (loading && !stats) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Loading intensity analysis...</p>
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-2">📊</div>
                <p className="text-gray-500">No intensity data available</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-full space-y-8">
            {/* Header */}
            <div className="text-center bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <h2 className="text-2xl sm:text-3xl font-bold text-purple-700">
                    {getScoreTypeDisplayName(scoreType)} Intensity Analysis
                </h2>
                <p className="text-gray-600 mt-2">
                    Analyze practice intensity and {getScoreTypeDisplayName(scoreType).toLowerCase()} trends together
                </p>
            </div>

            {/* Filters Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Analysis Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Score Type Selector */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Score Metric
                        </label>
                        <CategoryDropdownScores 
                            selectHandler={handleScoreTypeChange} 
                            value={scoreType} 
                        />
                    </div>

                    {/* Minimum Attempts Threshold */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Minimum Attempts Per Day
                        </label>
                        <Select 
                            onChange={handleLowlimitChange}
                            options={stats.counts}
                            placeholder="Select minimum attempts..."
                            className="text-sm"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: '42px',
                                    borderColor: '#d1d5db',
                                    '&:hover': {
                                        borderColor: '#9ca3af'
                                    }
                                })
                            }}
                        />
                        <p className="text-xs text-gray-500">
                            Filter days with fewer than selected attempts
                        </p>
                    </div>
                </div>
            </div>

            {/* Charts Area - Only this part re-renders */}
            <ChartsAreaIntensity 
                stats={stats} 
                loading={chartsLoading} 
                scoreType={scoreType}
                scoreTypeDisplayName={getScoreTypeDisplayName(scoreType)}
            />
        </div>
    );
}

export default UserSentencesIntensity;